import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import LeftNav from "../components/common/LeftNav";
import TopNav from "../components/common/TopNav";
import crmService from "../../services/crmService";
import userService from "../../services/userService";
import analyticsService from "../../services/analyticsService";
import "../assets/styles/AdminCRM.css";

// Icons and images
import addIcon from "../assets/img/add-circle.svg";
import filterIcon from "../assets/img/filterscrm.svg";
import searchIcon from "../assets/img/crmactivityweb.svg";
import callIcon from "../assets/img/call.svg";
import whatsappIcon from "../assets/img/call.svg";
import editIcon from "../assets/img/editcrm.svg";
import deleteIcon from "../assets/img/delete.svg";
import arrowDownIcon from "../assets/img/downarrow.svg";
import phoneIcon from "../assets/img/call.svg";
import emailIcon from "../assets/img/crmactivityweb.svg";
import locationIcon from "../assets/img/location.svg";

export default function AdminCRM() {
  const navigate = useNavigate();

  // State management
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const [expandedCard, setExpandedCard] = useState(null);

  // Filter states
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    source: "",
    assignedTo: "",
    startDate: "",
    endDate: "",
    priority: ""
  });

  // Add Lead Modal states
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [addLeadForm, setAddLeadForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    alternate_phone: "",
    source: "",
    status: "New",
    priority: "medium",
    budget_min: "",
    budget_max: "",
    requirement_type: "buy",
    property_type: "residential",
    preferred_location: "",
    notes: "",
    assigned_to: ""
  });

  // Configuration data
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [users, setUsers] = useState([]);

  // User registration modal states
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [addUserForm, setAddUserForm] = useState({
    registerAs: 'select',
    firstName: '',
    lastName: '',
    fullName: '',
    email: '',
    phone: '',
    panNo: '',
    gstNo: '',
    reraNo: '',
    tanNo: '',
    refId: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    password: '',
    confirmPassword: ''
  });

  // Dynamic state/city dropdown data
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [allCitiesData, setAllCitiesData] = useState({});

  // Registration states
  const [emailOTP, setEmailOTP] = useState('');
  const [phoneOTP, setPhoneOTP] = useState('');
  const [emailOTPSent, setEmailOTPSent] = useState(false);
  const [phoneOTPSent, setPhoneOTPSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Load initial data
  useEffect(() => {
    loadLeads();
    loadConfigurationData();
    loadUsers();
  }, []);

  const loadLeads = async (filterParams = {}) => {
    setLoading(true);
    try {
      console.log('🔍 Loading leads with filters:', filterParams);
      const response = await crmService.getLeads(filterParams);

      if (response.success) {
        const leadsData = response.data?.data || response.data || [];
        console.log('✅ Leads loaded successfully:', leadsData.length);
        setLeads(leadsData);
        setFilteredLeads(leadsData);
      } else {
        console.error('❌ Failed to load leads:', response.message);
        toast.error(response.message || 'Failed to load leads');
      }
    } catch (error) {
      console.error('❌ Error loading leads:', error);
      toast.error('Error loading leads');
    } finally {
      setLoading(false);
    }
  };

  const loadConfigurationData = async () => {
    try {
      const [statusResponse, sourceResponse] = await Promise.all([
        crmService.getLeadStatuses(),
        crmService.getLeadSources()
      ]);

      if (statusResponse.success) {
        setLeadStatuses(statusResponse.data || []);
      }
      if (sourceResponse.success) {
        setLeadSources(sourceResponse.data || []);
      }
    } catch (error) {
      console.error('Error loading configuration data:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await userService.getAllUsers();
      if (response.success) {
        const usersData = response.data?.data?.users || response.data?.users || response.data || [];
        setUsers(usersData);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  // Search and filter functions
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setFilteredLeads(leads);
      return;
    }

    const searchResults = leads.filter(lead =>
      lead.first_name?.toLowerCase().includes(query.toLowerCase()) ||
      lead.last_name?.toLowerCase().includes(query.toLowerCase()) ||
      lead.email?.toLowerCase().includes(query.toLowerCase()) ||
      lead.phone?.includes(query) ||
      lead.preferred_location?.toLowerCase().includes(query.toLowerCase())
    );

    setFilteredLeads(searchResults);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "All") {
      setFilteredLeads(leads);
    } else {
      const filtered = leads.filter(lead => lead.status === tab);
      setFilteredLeads(filtered);
    }
  };

  const handleFilterApply = () => {
    const filterParams = {};
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        filterParams[key] = filters[key];
      }
    });

    loadLeads(filterParams);
    setShowFilterModal(false);
  };

  const handleAddLead = async () => {
    if (!addLeadForm.first_name || !addLeadForm.phone) {
      toast.warning("Please fill in required fields (First Name and Phone)");
      return;
    }

    try {
      setLoading(true);
      console.log('📝 Creating new lead:', addLeadForm);

      const response = await crmService.createLead(addLeadForm);

      if (response.success) {
        console.log('✅ Lead created successfully');
        setShowAddLeadModal(false);
        setAddLeadForm({
          first_name: "",
          last_name: "",
          email: "",
          phone: "",
          alternate_phone: "",
          source: "",
          status: "New",
          priority: "medium",
          budget_min: "",
          budget_max: "",
          requirement_type: "buy",
          property_type: "residential",
          preferred_location: "",
          notes: "",
          assigned_to: ""
        });
        loadLeads(); // Refresh the leads list
      } else {
        toast.error(response.message || 'Failed to create lead');
      }
    } catch (error) {
      console.error('Error creating lead:', error);
      toast.error('Error creating lead');
    } finally {
      setLoading(false);
    }
  };

  const handleLeadClick = (leadId) => {
    navigate(`/admin-crm-lead/${leadId}`);
  };

  const handleStatusUpdate = async (leadId, newStatus) => {
    try {
      const response = await crmService.updateLeadStatus(leadId, newStatus);
      if (response.success) {
        loadLeads(); // Refresh leads
      } else {
        toast.error(response.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error updating status');
    }
  };

  const toggleCardExpansion = (leadId) => {
    setExpandedCard(expandedCard === leadId ? null : leadId);
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'New': 'badge-new',
      'Contacted': 'badge-contacted',
      'Qualified': 'badge-qualified',
      'Converted': 'badge-converted',
      'Dead': 'badge-dead',
      'Hot': 'badge-hot',
      'Warm': 'badge-warm',
      'Cold': 'badge-cold'
    };
    return statusMap[status] || 'badge-default';
  };

  const getPriorityBadgeClass = (priority) => {
    const priorityMap = {
      'urgent': 'priority-urgent',
      'high': 'priority-high',
      'medium': 'priority-medium',
      'low': 'priority-low'
    };
    return priorityMap[priority] || 'priority-medium';
  };

  // Get unique statuses from leads for tabs
  const availableStatuses = useMemo(() => {
    const statusSet = new Set(leads.map(lead => lead.status).filter(Boolean));
    return ['All', ...Array.from(statusSet)];
  }, [leads]);

  return (
    <div className="d-flex">
      <LeftNav />
      <div className="main-content flex-grow-1">
        <TopNav />
        <div className="p-4">
          <div className="crm-container">

            {/* Header Section */}
            <div className="crm-header mb-4">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h2 className="crm-title mb-2">CRM Dashboard</h2>
                  <p className="crm-subtitle">
                    Total Leads: {filteredLeads.length} | Today: {filteredLeads.filter(lead =>
                      new Date(lead.created_at).toDateString() === new Date().toDateString()
                    ).length}
                  </p>
                </div>
                <div className="d-flex gap-3">
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => setShowFilterModal(true)}
                  >
                    <img src={filterIcon} alt="Filter" width="16" height="16" className="me-2" />
                    Filter
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowAddLeadModal(true)}
                  >
                    <img src={addIcon} alt="Add" width="16" height="16" className="me-2" />
                    Add Lead
                  </button>
                </div>
              </div>

              {/* Search Bar */}
              <div className="crm-search-bar mt-3">
                <div className="position-relative">
                  <img src={searchIcon} alt="Search" className="search-icon" width="20" height="20" />
                  <input
                    type="text"
                    className="form-control search-input"
                    placeholder="Search leads by name, email, phone, or location..."
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </div>
              </div>

              {/* Status Tabs */}
              <div className="crm-tabs mt-3">
                <div className="d-flex gap-2 flex-wrap">
                  {availableStatuses.map(status => (
                    <button
                      key={status}
                      className={`tab-button ${activeTab === status ? 'active' : ''}`}
                      onClick={() => handleTabChange(status)}
                    >
                      {status}
                      {status !== 'All' && (
                        <span className="tab-count ms-2">
                          {leads.filter(lead => lead.status === status).length}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Leads List */}
            <div className="crm-leads-container">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <div className="mt-3" style={{ color: '#888' }}>Loading...</div>
                </div>
              ) : filteredLeads.length === 0 ? (
                <div className="empty-state text-center py-5">
                  <h5>No leads found</h5>
                  <p>Try adjusting your search or filters, or create a new lead.</p>
                </div>
              ) : (
                <div className="leads-grid">
                  {filteredLeads.map(lead => (
                    <div key={lead.id} className="lead-card">
                      <div className="lead-card-header" onClick={() => toggleCardExpansion(lead.id)}>
                        <div className="lead-info">
                          <div className="lead-name">
                            {lead.first_name} {lead.last_name}
                          </div>
                          <div className="lead-contact">
                            <img src={phoneIcon} alt="Phone" width="14" height="14" className="me-1" />
                            {lead.phone}
                          </div>
                          {lead.email && (
                            <div className="lead-contact">
                              <img src={emailIcon} alt="Email" width="14" height="14" className="me-1" />
                              {lead.email}
                            </div>
                          )}
                        </div>
                        <div className="lead-badges">
                          <span className={`status-badge ${getStatusBadgeClass(lead.status)}`}>
                            {lead.status || 'New'}
                          </span>
                          <span className={`priority-badge ${getPriorityBadgeClass(lead.priority)}`}>
                            {lead.priority || 'Medium'}
                          </span>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="lead-actions">
                        <button className="action-btn call-btn" title="Call">
                          <img src={callIcon} alt="Call" width="16" height="16" />
                        </button>
                        <button className="action-btn whatsapp-btn" title="WhatsApp">
                          <img src={whatsappIcon} alt="WhatsApp" width="16" height="16" />
                        </button>
                        <button
                          className="action-btn edit-btn"
                          title="View/Edit"
                          onClick={() => handleLeadClick(lead.id)}
                        >
                          <img src={editIcon} alt="Edit" width="16" height="16" />
                        </button>
                        <button
                          className="action-btn expand-btn"
                          onClick={() => toggleCardExpansion(lead.id)}
                        >
                          <img
                            src={arrowDownIcon}
                            alt="Expand"
                            width="16"
                            height="16"
                            className={expandedCard === lead.id ? 'rotated' : ''}
                          />
                        </button>
                      </div>

                      {/* Expanded Content */}
                      {expandedCard === lead.id && (
                        <div className="lead-expanded">
                          <div className="row">
                            <div className="col-md-6">
                              <div className="lead-detail">
                                <label>Source:</label>
                                <span>{lead.source || 'Unknown'}</span>
                              </div>
                              <div className="lead-detail">
                                <label>Budget:</label>
                                <span>
                                  {lead.budget_min && lead.budget_max
                                    ? `₹${lead.budget_min}L - ₹${lead.budget_max}L`
                                    : 'Not specified'
                                  }
                                </span>
                              </div>
                              <div className="lead-detail">
                                <label>Property Type:</label>
                                <span>{lead.property_type || 'Any'}</span>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="lead-detail">
                                <label>Requirement:</label>
                                <span>{lead.requirement_type || 'Buy'}</span>
                              </div>
                              <div className="lead-detail">
                                <label>Location:</label>
                                <span>{lead.preferred_location || 'Any'}</span>
                              </div>
                              <div className="lead-detail">
                                <label>Created:</label>
                                <span>{new Date(lead.created_at).toLocaleDateString()}</span>
                              </div>
                            </div>
                          </div>

                          {lead.notes && (
                            <div className="lead-notes mt-2">
                              <label>Notes:</label>
                              <p>{lead.notes}</p>
                            </div>
                          )}

                          {/* Quick Status Update */}
                          <div className="status-update mt-3">
                            <label>Update Status:</label>
                            <select
                              className="form-select"
                              value={lead.status}
                              onChange={(e) => handleStatusUpdate(lead.id, e.target.value)}
                            >
                              {leadStatuses.map(status => (
                                <option key={status.id || status} value={status.name || status}>
                                  {status.name || status}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add Lead Modal */}
            {showAddLeadModal && (
              <div className="modal-overlay" onClick={() => setShowAddLeadModal(false)}>
                <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h5 className="modal-title">Add New Lead</h5>
                    <button className="modal-close" onClick={() => setShowAddLeadModal(false)}>×</button>
                  </div>
                  <div className="modal-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>First Name *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={addLeadForm.first_name}
                            onChange={(e) => setAddLeadForm({...addLeadForm, first_name: e.target.value})}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Last Name</label>
                          <input
                            type="text"
                            className="form-control"
                            value={addLeadForm.last_name}
                            onChange={(e) => setAddLeadForm({...addLeadForm, last_name: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Phone *</label>
                          <input
                            type="tel"
                            className="form-control"
                            value={addLeadForm.phone}
                            onChange={(e) => setAddLeadForm({...addLeadForm, phone: e.target.value})}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Email</label>
                          <input
                            type="email"
                            className="form-control"
                            value={addLeadForm.email}
                            onChange={(e) => setAddLeadForm({...addLeadForm, email: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Source</label>
                          <select
                            className="form-control"
                            value={addLeadForm.source}
                            onChange={(e) => setAddLeadForm({...addLeadForm, source: e.target.value})}
                          >
                            <option value="">Select Source</option>
                            {leadSources.map(source => (
                              <option key={source.id || source} value={source.name || source}>
                                {source.name || source}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Priority</label>
                          <select
                            className="form-control"
                            value={addLeadForm.priority}
                            onChange={(e) => setAddLeadForm({...addLeadForm, priority: e.target.value})}
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="urgent">Urgent</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Budget Min (Lakhs)</label>
                          <input
                            type="number"
                            className="form-control"
                            value={addLeadForm.budget_min}
                            onChange={(e) => setAddLeadForm({...addLeadForm, budget_min: e.target.value})}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Budget Max (Lakhs)</label>
                          <input
                            type="number"
                            className="form-control"
                            value={addLeadForm.budget_max}
                            onChange={(e) => setAddLeadForm({...addLeadForm, budget_max: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Requirement Type</label>
                          <select
                            className="form-control"
                            value={addLeadForm.requirement_type}
                            onChange={(e) => setAddLeadForm({...addLeadForm, requirement_type: e.target.value})}
                          >
                            <option value="buy">Buy</option>
                            <option value="rent">Rent</option>
                            <option value="lease">Lease</option>
                            <option value="investment">Investment</option>
                          </select>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Property Type</label>
                          <select
                            className="form-control"
                            value={addLeadForm.property_type}
                            onChange={(e) => setAddLeadForm({...addLeadForm, property_type: e.target.value})}
                          >
                            <option value="residential">Residential</option>
                            <option value="commercial">Commercial</option>
                            <option value="industrial">Industrial</option>
                            <option value="agricultural">Agricultural</option>
                            <option value="plot">Plot</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Preferred Location</label>
                      <input
                        type="text"
                        className="form-control"
                        value={addLeadForm.preferred_location}
                        onChange={(e) => setAddLeadForm({...addLeadForm, preferred_location: e.target.value})}
                        placeholder="e.g., Gachibowli, Hyderabad"
                      />
                    </div>

                    <div className="form-group">
                      <label>Assign To</label>
                      <select
                        className="form-control"
                        value={addLeadForm.assigned_to}
                        onChange={(e) => setAddLeadForm({...addLeadForm, assigned_to: e.target.value})}
                      >
                        <option value="">Select User</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.full_name || user.name || user.email}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Notes</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={addLeadForm.notes}
                        onChange={(e) => setAddLeadForm({...addLeadForm, notes: e.target.value})}
                        placeholder="Additional notes or requirements..."
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setShowAddLeadModal(false)}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleAddLead}
                      disabled={loading}
                    >
                      {loading ? 'Creating...' : 'Create Lead'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Filter Modal */}
            {showFilterModal && (
              <div className="modal-overlay" onClick={() => setShowFilterModal(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h5 className="modal-title">Filter Leads</h5>
                    <button className="modal-close" onClick={() => setShowFilterModal(false)}>×</button>
                  </div>
                  <div className="modal-body">
                    <div className="form-group">
                      <label>Status</label>
                      <select
                        className="form-control"
                        value={filters.status}
                        onChange={(e) => setFilters({...filters, status: e.target.value})}
                      >
                        <option value="">All Statuses</option>
                        {leadStatuses.map(status => (
                          <option key={status.id || status} value={status.name || status}>
                            {status.name || status}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Source</label>
                      <select
                        className="form-control"
                        value={filters.source}
                        onChange={(e) => setFilters({...filters, source: e.target.value})}
                      >
                        <option value="">All Sources</option>
                        {leadSources.map(source => (
                          <option key={source.id || source} value={source.name || source}>
                            {source.name || source}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Assigned To</label>
                      <select
                        className="form-control"
                        value={filters.assignedTo}
                        onChange={(e) => setFilters({...filters, assignedTo: e.target.value})}
                      >
                        <option value="">All Users</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.full_name || user.name || user.email}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Start Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={filters.startDate}
                            onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>End Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={filters.endDate}
                            onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setFilters({
                          status: "",
                          source: "",
                          assignedTo: "",
                          startDate: "",
                          endDate: "",
                          priority: ""
                        });
                        setShowFilterModal(false);
                        loadLeads();
                      }}
                    >
                      Clear Filters
                    </button>
                    <button className="btn btn-primary" onClick={handleFilterApply}>
                      Apply Filters
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}